import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import OpenStreetMapView from '../../organisms/Map/OpenStreetMapView';
import './index.scss';

const LOCATION_MARKER_ID = 'location-preview-marker';
const LOCATION_MAP_DEFAULT_CENTER = Object.freeze({
  lat: 39.8283,
  lng: -98.5795,
});
const LOCATION_MAP_DEFAULT_ZOOM = 3;
const LOCATION_MAP_FOCUSED_ZOOM = 12;
const LOCATION_MAP_INTERACTION_HINT = 'Drag the pin or click anywhere on the map to refine your position.';

function buildLocationMarker(coordinates) {
  if (!coordinates) {
    return [];
  }

  return [{
    id: LOCATION_MARKER_ID,
    name: 'Selected location',
    type: 'POINT',
    coordinate: coordinates,
    metadata: {},
  }];
}

export default function LocationMiniMap({
  coordinates = null,
  disabled = false,
  isResolving = false,
  onCoordinatesChange = undefined,
}) {
  const hasCoordinates = Boolean(
    coordinates
    && Number.isFinite(coordinates.lat)
    && Number.isFinite(coordinates.lng)
  );
  const markers = useMemo(
    () => buildLocationMarker(hasCoordinates ? coordinates : null),
    [coordinates, hasCoordinates]
  );
  const draggableMarkerIds = useMemo(
    () => (!disabled && hasCoordinates ? [LOCATION_MARKER_ID] : []),
    [disabled, hasCoordinates]
  );
  const center = hasCoordinates ? coordinates : LOCATION_MAP_DEFAULT_CENTER;
  const zoom = hasCoordinates ? LOCATION_MAP_FOCUSED_ZOOM : LOCATION_MAP_DEFAULT_ZOOM;

  let helperText = LOCATION_MAP_INTERACTION_HINT;
  if (disabled) {
    helperText = 'Map updates are temporarily unavailable while your location is being updated.';
  } else if (isResolving) {
    helperText = 'Resolving location from the map...';
  }

  const handleMapClick = disabled || typeof onCoordinatesChange !== 'function'
    ? undefined
    : (nextCoordinates) => onCoordinatesChange(nextCoordinates);

  const handleMarkerDragEnd = disabled || typeof onCoordinatesChange !== 'function'
    ? undefined
    : (_markerId, nextCoordinates) => onCoordinatesChange(nextCoordinates);

  return (
    <div className="location-mini-map" aria-live="polite">
      <OpenStreetMapView
        className="location-mini-map__map"
        ariaLabel="Location preview map"
        loadingLabel="Loading location map..."
        center={center}
        zoom={zoom}
        minZoom={2}
        markers={markers}
        onMapClick={handleMapClick}
        draggableMarkerIds={draggableMarkerIds}
        onMarkerDragEnd={handleMarkerDragEnd}
        disableMarkerPopups
      />
      <p className="location-mini-map__hint">{helperText}</p>
    </div>
  );
}

LocationMiniMap.propTypes = {
  coordinates: PropTypes.shape({
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
  }),
  disabled: PropTypes.bool,
  isResolving: PropTypes.bool,
  onCoordinatesChange: PropTypes.func,
};
